import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { HPACS_URL } from '../../../../shared/env';
import { useLinkByCode } from '../../../../shared/hooks/caseHook';
import { WideModal } from '../../common/components/WideModal';

const LinkCodeFormWrapper = styled.form`
  position: relative;
`;

const LinkCodeFormInputWrapper = styled.div`
  display: flex;
  align-items: center;
  jusity-content: center;
  max-width: 194px;
  width: 100%;
  height: 58px;
  box-sizing: border-box;
  padding: 0 26px;
  border: 1px solid var(--HScan_Light_Blue, #2e5be0);
  border-radius: 58px;
  color: white;
  background: white;

  &:focus-within {
    outline: -webkit-focus-ring-color auto 1px;
  }
`;

const LinkCodeFormInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  font-size: 16px;

  &:placeholder {
    color: var(--HPACS_Text_Light_Grey, #a3a6ab);
  }

  @media (max-width: 1129px) {
    padding-right: 30px;
  }
`;

const LinkCodeFormButton = styled.button`
  margin-top: 14px;
  max-width: 194px;
  width: 100%;
  height: 58px;
  border: none;
  border-radius: 58px;
  font-size: 14px;
  font-weight: 700;
  color: white;
  background: var(--HScan_Light_Blue, #2e5be0);
  cursor: pointer;

  &:after {
    content: '〉';
    font-size: 20px;
    vertical-align: middle;
    margin-left: 10px;
  }

  @media (max-width: 1129px) {
    position: absolute;
    top: 0;
    right: 0;
    margin: 0;
    width: 52px;
    border-radius: 0 58px 58px 0;
    font-size: 0;
  }
`;

export enum LinkCodeFormError {
  NO_CODE = 'NO_CODE',
  INVALID_INPUT = 'INVALID_INPUT',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

interface LinkCodeFormProps {
  onError?: (error: LinkCodeFormError) => void;
}
export const LinkCodeForm = (props: LinkCodeFormProps) => {
  const { onError } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.landing.cards.3',
  });
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const { data, refetch, isSuccess, isError, error } = useLinkByCode(code, {
    enabled: false,
  });
  useEffect(() => {
    if (isSuccess && data) {
      const id = data.id;
      const url = `${HPACS_URL}/linkLogin/${id}`; // URL to env
      window.open(url, '_blank');
    }
  }, [data, isSuccess]);
  useEffect(() => {
    if (isError) {
      if (
        error.response &&
        error.response.status === 404 &&
        error.response.data.detail.code === '4041'
      ) {
        onError?.(LinkCodeFormError.NO_CODE);
      } else {
        onError?.(LinkCodeFormError.UNKNOWN_ERROR);
      }
      setOpen(true);
    }
  }, [error?.response, isError, onError]);

  const onSubmitShareCode = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (code !== '') {
      refetch();
    } else {
      onError?.(LinkCodeFormError.INVALID_INPUT);
    }
  };

  return (
    <LinkCodeFormWrapper onSubmit={onSubmitShareCode}>
      <LinkCodeFormInputWrapper>
        <LinkCodeFormInput
          type="tel"
          onInput={e => setCode(e.currentTarget.value)}
          placeholder={`${t('linkCodeForm.placeholder')}`}
        />
      </LinkCodeFormInputWrapper>
      <LinkCodeFormButton>{t('button')}</LinkCodeFormButton>
      <WideModal open={open} onClose={() => setOpen(false)}>
        <div>{t('linkCodeForm.errorMessage')}</div>
      </WideModal>
    </LinkCodeFormWrapper>
  );
};
